import { useSelector } from "react-redux";

import { openWaitPause, PAUSE_CANCEL_BUTTON, PauseCardData } from "../shared/hitl/actionPause";

/** The threads whose messages this hook can see, in the two shapes the store keeps them in. */
export const threadMessages = (chat: any, threadKey: string): any[] => {
  // A ticket thread's messages sit at the top of the slice, under the thread the page has open.
  if (chat?.aiThreadId != null && String(chat.aiThreadId) === threadKey) return chat.messages || [];

  // The assistant surface keeps one message list per thread it has loaded, and renders from
  // there -- so reading only the top-level list would find nothing at all on that page.
  const thread = (chat?.userThreads || []).find((entry: any) => String(entry?.ai_thread_id) === threadKey);
  return thread?.messages || [];
};

/**
 * The thread's last message that carries a review, or null. The last card in the thread, not the
 * last card of some kind: an older one that was answered and followed by something else is
 * history, and history does not shut (or relabel) a composer.
 */
export const lastHumanReviewMessage = (messages: any[]): any | null => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.human_review) return messages[i];
  }
  return null;
};

const useThreadWait = (aiThreadId: string | number | null | undefined): PauseCardData | null => {
  const last = useSelector((state: any) => {
    const threadKey = aiThreadId != null ? String(aiThreadId) : null;
    if (!threadKey) return null;
    return lastHumanReviewMessage(threadMessages(state?.chat, threadKey));
  });
  return openWaitPause(last?.human_review, last?.human_review_status)?.card ?? null;
};

export function composeChatDisabledReason(wait: PauseCardData | null, computerUseReason: string | null): string | null {
  if (!wait || wait.chat_reply) return computerUseReason;
  const reason = "Chat is disabled while Theo waits for this check to finish.";
  return wait.buttons.some((button) => button.id === PAUSE_CANCEL_BUTTON) ? `${reason} You can cancel the check.` : reason;
}

export function deleteThreadWarning(scheduleWarning: string | null, wait: PauseCardData | null): string | null {
  if (!wait) return scheduleWarning;
  const title = wait.title.trim();
  const waitWarning = title
    ? `The pending check "${title}" will also be cancelled.`
    : "The pending check will also be cancelled.";
  return scheduleWarning ? `${scheduleWarning} ${waitWarning}` : waitWarning;
}

export default useThreadWait;
